import {
  embedFiles,
  getWorkspaceId,
  ListZoFileResponse,
  useAppSelector,
  useCreateZoFileObjectsMutation,
  useEmbedFileMutation,
  useGetIndexingStatusMutation,
  useListZoFilePrefixesMutation,
  useUpdateZoFileObjectsMutation,
  finService,
  TrackEventType,
  getWorkspace,
} from '@zonofi/seller-rtk-store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { SIGNUP_ONBOARDING } from '../navigation';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

export const useRetrainAgentScreen = () => {
  const navigate = useNavigate();
  const workspace = useAppSelector(getWorkspace);
  const workspaceId = useAppSelector(getWorkspaceId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [knowledgeFiles, setKnowledgeFiles] = useState<ListZoFileResponse[]>(
    []
  );
  const [files, setFiles] = useState<File[]>([]);
  const [s3Keys, setS3Keys] = useState<string[]>([]);
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const [embedFilesObj, setEmbedFilesObj] = useState<embedFiles[]>([]);
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const [trainingCompleted, setTrainingCompleted] = useState<boolean>(false);
  const [uploadingSampleFile, setUploadingSampleFile] =
    useState<boolean>(false);
  const [embedFilesReq, embedFilesRes] = useEmbedFileMutation();
  const [fetchFilesReq, fetchFilesRes] = useListZoFilePrefixesMutation();
  const [fileActionReq, fileActionRes] = useUpdateZoFileObjectsMutation();
  const [getIndexingStatusReq, getIndexingStatusRes] =
    useGetIndexingStatusMutation();
  const [uploadFilesReq, uploadFilesRes] = useCreateZoFileObjectsMutation();

  const extractS3Info = (presignedUrl: string) => {
    const url = new URL(presignedUrl);

    const hostParts = url.hostname.split('.');
    const bucket = hostParts[0];

    const key = url.pathname.startsWith('/')
      ? url.pathname.slice(1)
      : url.pathname;

    return { bucket, key };
  };

  const performAction = useCallback((action: string, suffix: string) => {
    fileActionReq({
      params: {
        sellerWorkspaceId: workspaceId,
      },
      body: {
        request: [
          {
            action: action,
            documentType: 'knowledge',
            suffix: suffix,
            type: 'file',
          },
        ],
      },
    });
  }, []);

  const deleteKnowledgeFile = useCallback((suffix: string) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== suffix));
    performAction('delete', suffix);
  }, []);

  const downloadKnowledgeFile = useCallback((suffix: string) => {
    performAction('download', suffix);
  }, []);

  const viewKnowledgeFile = useCallback((suffix: string) => {
    performAction('view', suffix);
  }, []);

  const fetchFiles = () => {
    fetchFilesReq({
      params: {
        sellerWorkspaceId: workspaceId,
      },
      body: {
        documentType: 'knowledge',
      },
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const sanitizedFiles = Array.from(files).map((file) => {
        const sanitizedFileName = file.name.replace(/\s+/g, '');
        return new File([file], sanitizedFileName, { type: file.type });
      });
      setFiles((prev) => [...prev, ...sanitizedFiles]);
    }
  };

  const handleSelect = () => {
    inputRef.current?.click();
  };

  const navigateBackToAgentScreen = () => {
    // finService.trackEvent(TrackEventType.SIGNUP_ONBOARDING_STARTED);
    navigate(SIGNUP_ONBOARDING.TEST_AGENT.path, { replace: true });
  };

  const startAgentTraining = () => {
    setIsTraining(true);
    setTrainingCompleted(false);
    embedFilesReq({
      params: {
        sellerWorkspaceId: workspaceId,
      },
      files: embedFilesObj,
    });
  };

  const startTrainingPolling = useCallback(() => {
    const pollApi = async () => {
      try {
        await getIndexingStatusReq({
          params: {
            sellerWorkspaceId: workspaceId,
          },
          body: {
            s3Keys: s3Keys,
          },
        });
      } catch (error) {
        console.log('Polling error: ', error);
      }
    };

    if (s3Keys.length > 0) {
      pollApi();
      intervalRef.current = setInterval(pollApi, 3000);

      timeoutRef.current = setTimeout(() => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        setTrainingCompleted(true);
        toast.error('Training timeout - please try again');
      }, 60000);
    } else {
      setTrainingCompleted(true);
    }
  }, [s3Keys, workspaceId, getIndexingStatusReq]);

  const uploadFilesToS3 = () => {
    const uploadReq = files.map((file) => {
      return {
        name: file.name,
        suffix: file.name,
        documentType: 'knowledge',
        type: 'file',
      };
    });
    uploadFilesReq({
      params: {
        sellerWorkspaceId: workspaceId,
      },
      body: {
        uploadReq: uploadReq,
      },
    });
    setIsFileUploading(true);
  };

  const fetchSampleFile = async (): Promise<File> => {
    const fileName = `${workspace?.industry}.txt`;
    const fileUrl = `https://zotok-marketing.s3.ap-south-1.amazonaws.com/bot-traning-files/${fileName}`;
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error('Failed to fetch sample file');
    const blob = await response.blob();

    return new File([blob], fileName, { type: 'text/plain' });
  };

  const viewSampleFile = async () => {
    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result as string;
        navigate(SIGNUP_ONBOARDING.VIEW_SAMPLE_FILE.path, {
          state: { fileContent: content },
        });
      };

      reader.onerror = () => {
        alert('Error reading file');
      };

      const file = await fetchSampleFile();
      reader.readAsText(file);
    } catch (e) {
      console.error('Error loading sample file:', e);
    }
  };

  const uploadSampleFile = async () => {
    const file = await fetchSampleFile();
    setUploadingSampleFile(true);
    setFiles([file]);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (fetchFilesRes?.isSuccess && fetchFilesRes?.data) {
      const filteredFiles = fetchFilesRes?.data?.data?.files?.filter(
        (item) => item?.name !== 'readMe.txt'
      );

      setKnowledgeFiles(filteredFiles);
    }
  }, [fetchFilesRes]);

  useEffect(() => {
    if (!isEmpty(files)) uploadFilesToS3();
  }, [files]);

  useEffect(() => {
    const uploadFiles = async () => {
      if (uploadFilesRes?.isSuccess && uploadFilesRes?.data) {
        const data = uploadFilesRes?.data?.data || [];
        const keys: string[] = [];
        const extractedObjects: embedFiles[] = [];
        await Promise.all(
          data.map(async (item, index) => {
            const file = files[index];
            const object = extractS3Info(item?.presignedUrl);
            keys.push(object.key);
            extractedObjects.push(object);

            try {
              const uploadToS3Response = await fetch(item.presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                  'Content-Type': file.type,
                },
              });

              if (!uploadToS3Response.ok) {
                toast.error('Failed to upload file to S3');
                throw new Error('Failed to upload file to S3');
              }
            } catch (e) {
              toast.error('Error occured file uploading the files');
            } finally {
              setFiles([]);
              setUploadingSampleFile(false);
              fetchFiles();
              setIsFileUploading(false);
            }
          })
        );

        setEmbedFilesObj(extractedObjects);
        setS3Keys(keys);
      } else {
        setIsFileUploading(false);
        toast.error('Error occured while uploading the files');
      }
    };
    uploadFiles();
  }, [uploadFilesRes]);

  useEffect(() => {
    if (embedFilesRes?.isSuccess) {
      // Start training polling instead of navigating
      startTrainingPolling();
      console.log('test: api success');
    } else if (embedFilesRes?.isError) {
      setIsTraining(false);
      toast.error('Failed to start agent training');
    }
  }, [embedFilesRes, startTrainingPolling]);

  useEffect(() => {
    if (getIndexingStatusRes?.isSuccess && getIndexingStatusRes?.data) {
      const allIndexed = getIndexingStatusRes.data.data.every((item) =>
        item.tags.some((tag) => tag.Key === 'indexed' && tag.Value === 'true')
      );

      if (allIndexed) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setTrainingCompleted(true);
        toast.success('Agent training completed successfully!');
      }
    } else if (getIndexingStatusRes?.isError) {
      console.log('Indexing status error:', getIndexingStatusRes.error);
    }
  }, [getIndexingStatusRes]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (fileActionRes?.isSuccess) {
      const data = fileActionRes?.data?.data?.[0];
      if (data) {
        const action = fileActionRes?.originalArgs?.body?.request[0]?.action;
        switch (action) {
          case 'view':
          case 'download':
            window.open(data.presignedUrl, '_blank', 'noopener, noreferrer');
            break;
          case 'delete':
            toast.success('Deleted Successfully');
            // Add a small delay to ensure server has processed the delete
            setTimeout(() => {
              fetchFiles();
            }, 500);
            break;
          default:
            break;
        }
      }
    }
  }, [fileActionRes]);

  return {
    areFilesBeingFetched: fetchFilesRes?.isLoading || isFileUploading,
    deleteKnowledgeFile,
    downloadKnowledgeFile,
    embedFilesObj,
    files,
    handleFileChange,
    handleSelect,
    inputRef,
    isFileUploading,
    isTraining,
    knowledgeFiles,
    navigateBackToAgentScreen,
    s3Keys,
    setIsTraining,
    setTrainingCompleted,
    startAgentTraining,
    trainingCompleted,
    uploadFilesToS3,
    uploadingSampleFile,
    uploadSampleFile,
    viewKnowledgeFile,
    viewSampleFile,
  };
};
